import { useCallback, useEffect, useRef, useState } from 'react'
import {
  CheckmarkCircle16Filled,
  DocumentSearch20Regular,
  FolderOpen20Regular,
  Search20Regular,
  ShieldCheckmark20Regular,
  Tag20Regular,
} from '@fluentui/react-icons'
import { usePrefersReducedMotion, useInView } from '../lib/hooks'
import { BrandMark } from './BrandMark'

type Result = {
  name: string
  path: string
  kind: string
  meta: string
  score: number
  source: 'Local PC' | 'Office server'
  ocr?: boolean
  passageLabel: string
  passageTitle: string
  passage: string
  facts: [string, string][]
}

type Scenario = {
  query: string
  total: number
  latency: number
  results: Result[]
}

/** Two scripted runs — one English, one Greek — matching the shipped EN/EL support. */
const scenarios: Scenario[] = [
  {
    query: 'supplier renewal terms',
    total: 24,
    latency: 62,
    results: [
      {
        name: 'Q2 supplier agreement.pdf',
        path: 'D:\\Contracts\\2026\\Asteria',
        kind: 'PDF',
        meta: '14 pages · modified 12 Mar',
        score: 94,
        source: 'Local PC',
        passageLabel: 'Best passage · page 4',
        passageTitle: 'Renewal and termination',
        passage:
          'The agreement renews automatically for successive twelve-month terms unless either party gives written notice ninety days before the renewal date.',
        facts: [
          ['Category', 'Contract · 96%'],
          ['Counterparty', 'Asteria Group'],
          ['Permissions', 'Windows ACL revalidated'],
        ],
      },
      {
        name: 'Framework agreement — Nordwind.docx',
        path: '\\\\office-fs\\Shared\\Legal',
        kind: 'DOCX',
        meta: '22 pages · indexed by server',
        score: 89,
        source: 'Office server',
        passageLabel: 'Best passage · page 9',
        passageTitle: 'Price review on renewal',
        passage:
          'Renewal is subject to a price review. The supplier shall submit revised rates no later than sixty days before the anniversary date.',
        facts: [
          ['Category', 'Contract · 91%'],
          ['Shared root', 'Legal (read)'],
          ['Permissions', 'Group access confirmed'],
        ],
      },
      {
        name: 'Procurement policy v3.pdf',
        path: '\\\\office-fs\\Shared\\Policies',
        kind: 'PDF',
        meta: '8 pages · semantic match',
        score: 81,
        source: 'Office server',
        passageLabel: 'Semantic passage · page 2',
        passageTitle: 'Recording renewal decisions',
        passage:
          'Contract owners must record every renewal decision in the register before the notice window closes, including the approving manager.',
        facts: [
          ['Match', 'Meaning, not wording'],
          ['Model', 'Embedding · pgvector'],
          ['Permissions', 'Policy scope applied'],
        ],
      },
    ],
  },
  {
    query: 'τιμολόγιο συντήρησης 2026',
    total: 11,
    latency: 48,
    results: [
      {
        name: 'Τιμολόγιο 2026-184.pdf',
        path: 'D:\\Λογιστήριο\\Τιμολόγια',
        kind: 'PDF',
        meta: 'Σαρωμένο · OCR ολοκληρώθηκε',
        score: 96,
        source: 'Local PC',
        ocr: true,
        passageLabel: 'Καλύτερο απόσπασμα · σελίδα 1',
        passageTitle: 'Ετήσια συντήρηση εξοπλισμού',
        passage:
          'Ετήσια συντήρηση εξοπλισμού για την περίοδο 01/2026 – 12/2026, συμπεριλαμβανομένων ανταλλακτικών και επιτόπιας υποστήριξης.',
        facts: [
          ['Κατηγορία', 'Τιμολόγιο · 94%'],
          ['Εξαγωγή', 'Tesseract · ell+eng'],
          ['Δικαιώματα', 'Τοπικός χρήστης'],
        ],
      },
      {
        name: 'Σύμβαση συντήρησης.docx',
        path: '\\\\office-fs\\Shared\\Συμβάσεις',
        kind: 'DOCX',
        meta: '11 σελίδες · διακομιστής',
        score: 92,
        source: 'Office server',
        passageLabel: 'Απόσπασμα · σελίδα 3',
        passageTitle: 'Πεδίο εφαρμογής υπηρεσιών',
        passage:
          'Η συντήρηση περιλαμβάνει προληπτικούς ελέγχους δύο φορές ετησίως και αποκατάσταση βλαβών εντός δύο εργάσιμων ημερών.',
        facts: [
          ['Κατηγορία', 'Σύμβαση · 90%'],
          ['Κοινός φάκελος', 'Συμβάσεις'],
          ['Δικαιώματα', 'Έλεγχος ACL'],
        ],
      },
      {
        name: 'Maintenance invoices Q1.xlsx',
        path: '\\\\office-fs\\Shared\\Finance',
        kind: 'XLSX',
        meta: 'Cross-language match',
        score: 84,
        source: 'Office server',
        passageLabel: 'Semantic passage · sheet 1',
        passageTitle: 'Equipment maintenance — Q1',
        passage:
          'Annual equipment maintenance billed quarterly. Greek and English records are matched by meaning across both languages.',
        facts: [
          ['Match', 'EL query → EN document'],
          ['Model', 'Embedding · pgvector'],
          ['Permissions', 'Finance group'],
        ],
      },
    ],
  },
]

type Phase = 'typing' | 'scanning' | 'results'

export function AppPreview() {
  const reduced = usePrefersReducedMotion()
  const { ref, inView } = useInView<HTMLDivElement>({ once: false, threshold: 0.25 })

  const [scenarioIndex, setScenarioIndex] = useState(0)
  const [typed, setTyped] = useState(reduced ? scenarios[0].query : '')
  const [phase, setPhase] = useState<Phase>(reduced ? 'results' : 'typing')
  const [visible, setVisible] = useState(reduced ? 3 : 0)
  const [selected, setSelected] = useState(0)
  const [manual, setManual] = useState(false)

  const timers = useRef<number[]>([])
  const scenario = scenarios[scenarioIndex]

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout)
    timers.current = []
  }, [])

  const at = useCallback((delay: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, delay))
  }, [])

  useEffect(() => {
    if (reduced || manual || !inView) return

    clearTimers()
    setTyped('')
    setPhase('typing')
    setVisible(0)
    setSelected(0)

    const { query, results } = scenario
    let clock = 320

    for (let i = 1; i <= query.length; i += 1) {
      at(clock, () => setTyped(query.slice(0, i)))
      clock += query[i - 1] === ' ' ? 90 : 52
    }

    clock += 380
    at(clock, () => setPhase('scanning'))

    clock += 780
    at(clock, () => setPhase('results'))

    for (let i = 1; i <= results.length; i += 1) {
      clock += 150
      at(clock, () => setVisible(i))
    }

    clock += 1400
    for (let i = 1; i < results.length; i += 1) {
      at(clock, () => setSelected(i))
      clock += 3000
    }

    clock += 900
    at(clock, () => setScenarioIndex(index => (index + 1) % scenarios.length))

    return clearTimers
  }, [at, clearTimers, inView, manual, reduced, scenario])

  const takeOver = (index: number) => {
    clearTimers()
    setManual(true)
    setPhase('results')
    setTyped(scenario.query)
    setVisible(scenario.results.length)
    setSelected(index)
  }

  const active = scenario.results[selected]
  const showResults = phase === 'results'
  const isGreek = scenarioIndex === 1

  return (
    <div className="preview" ref={ref}>
      <div className="preview-window ticked">
        <div className="preview-titlebar">
          <BrandMark size={17} />
          <strong>Libris</strong>
          <span className="preview-crumb mono">Search</span>
          <span className="preview-health mono">
            <i className="dot dot-ok" />
            Index current · 3 roots
          </span>
        </div>

        <div className="preview-body">
          <nav className="preview-rail" aria-hidden="true">
            <span className="is-active">
              <Search20Regular />
              <em>Search</em>
            </span>
            <span>
              <FolderOpen20Regular />
              <em>Files</em>
            </span>
            <span>
              <Tag20Regular />
              <em>Tags</em>
            </span>
            <span>
              <DocumentSearch20Regular />
              <em>Details</em>
            </span>
            <span>
              <ShieldCheckmark20Regular />
              <em>Review</em>
            </span>
          </nav>

          <div className="preview-main">
            <div className={phase === 'scanning' ? 'preview-search is-busy' : 'preview-search'}>
              <Search20Regular />
              <span className="preview-query" lang={isGreek ? 'el' : 'en'}>
                {typed}
                {phase === 'typing' && !reduced && <i className="caret" />}
              </span>
              <kbd className="mono">Ctrl K</kbd>
              <span className="scan-line" />
            </div>

            <div className="preview-scopes">
              <span className="chip is-on">
                <i className="dot dot-ok" />
                This PC
              </span>
              <span className="chip is-on">
                <i className="dot dot-brand" />
                Office server
              </span>
              <span className="chip">Filters</span>
              <span className="preview-count mono">
                {showResults ? `${scenario.total} results · ${scenario.latency} ms` : 'Searching…'}
              </span>
            </div>

            <div className="preview-split">
              {!showResults && (
                <div className="skeleton-list" aria-hidden="true">
                  <span style={{ ['--i' as string]: 0 }} />
                  <span style={{ ['--i' as string]: 1 }} />
                  <span style={{ ['--i' as string]: 2 }} />
                </div>
              )}
              <ul className="preview-results" hidden={!showResults}>
                {scenario.results.map((result, index) => (
                  <li key={result.name} className={index < visible ? 'is-in' : ''} style={{ ['--i' as string]: index }}>
                    <button
                      type="button"
                      className={selected === index && showResults ? 'is-selected' : ''}
                      onClick={() => takeOver(index)}
                      tabIndex={-1}
                    >
                      <span className={`kind kind-${result.kind.toLowerCase()}`}>{result.kind}</span>
                      <span className="result-text">
                        <strong lang={isGreek ? 'el' : 'en'}>{result.name}</strong>
                        <small className="mono">{result.path}</small>
                        <small>{result.meta}</small>
                      </span>
                      <span className="result-score">
                        <b className="mono">{result.score}</b>
                        <i>
                          <u style={{ ['--w' as string]: `${result.score}%` }} />
                        </i>
                        <em className={result.source === 'Local PC' ? 'src-local' : 'src-server'}>
                          {result.source === 'Local PC' ? 'LOCAL' : 'SERVER'}
                        </em>
                      </span>
                      {result.ocr && <span className="ocr-flag mono">OCR</span>}
                    </button>
                  </li>
                ))}
              </ul>

              {!showResults && <div className="skeleton-detail" aria-hidden="true" />}
              <div className="preview-detail is-in" hidden={!showResults} key={active.name}>
                <span className="detail-kicker mono">{active.passageLabel}</span>
                <h3 lang={isGreek ? 'el' : 'en'}>{active.passageTitle}</h3>
                <blockquote lang={isGreek ? 'el' : 'en'}>{active.passage}</blockquote>
                <dl>
                  {active.facts.map(([term, value]) => (
                    <div key={term}>
                      <dt>{term}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="detail-verified">
                  <CheckmarkCircle16Filled />
                  Source verified · opened from original location
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="preview-status mono">
          <span>sqlite · fts5 + vectors</span>
          <span>128,430 files indexed</span>
          <span>agent 96 MB</span>
          <span className="preview-hint">{manual ? 'Interactive' : 'Live preview'}</span>
        </div>
      </div>

      <div className="preview-glow" aria-hidden="true" />
    </div>
  )
}
